<template>
  <div>
    <Filters
      :initial-values
      :page-type="String(route.name)"
      :status-filter-option
      @apply-filters="handleApplyFilter"
    />

    <slot name="custom-content" />

    <RevCard>
      <RevLoadingScreen
        v-if="isLoading"
        class="py-72"
        :text="i18n(translations.loadingScreenLabel)"
      />
      <div v-if="!isLoading">
        <slot
          v-if="!hasData && !error"
          :has-filters-on="!isEmpty(formattedQueryParams)"
          name="empty-state"
          :total-count="results?.metadata?.totalCount"
        />

        <div v-if="hasData && !error" class="space-y-16 py-16">
          <div class="px-16">
            <slot name="title" :total-count="results?.metadata?.totalCount" />
          </div>

          <slot :data="results?.requests" name="table" />

          <RevPagination
            class="py-24"
            :current-page
            :current-page-label="
              i18n(translations.currentPage, { page: currentPage })
            "
            :navigation-aria-label="i18n(translations.navigationLabel)"
            :next-aria-label="i18n(translations.nextPage)"
            :page-aria-label="
              (page) => i18n(translations.paginationLabel, { page })
            "
            :page-count
            :previous-aria-label="i18n(translations.previousPage)"
            @click="handlePagination"
          />
        </div>
        <TechnicalIssueBlock
          v-if="error"
          :title="errorTitle"
          @try-again="refresh"
        />
      </div>
    </RevCard>
  </div>
</template>

<script setup lang="ts" generic="Values extends Record<string, unknown>">
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { mapValues } from '@backmarket/utils/object/mapValues'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevPagination } from '@ds/components/Pagination'

import type {
  Filter,
  GetRequestsResponse,
} from '~/api/seller-after-sales/types/requests.types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useFormattedQueryParams } from '~/scopes/customer-care/pages/customer-care/composables/useFormattedQueryParams'

import {
  EMPTY_FILTERS_VALUES,
  MULTISELECT_FILTERS_NAMES,
} from '../Filters/Filters.constants'
import Filters from '../Filters/Filters.vue'
import TechnicalIssueBlock from '../TechnicalIssueBlock/TechnicalIssueBlock.vue'

import translations from './CareDataContent.translations'

export interface StatusFilterOption {
  label: string
  value: string
}

type FetchValues = Partial<Values> & { pageNumber?: number; pageSize?: number }

const route = useRoute()

const DEFAULT_CURRENT_PAGE = 1
const REQUEST_COUNT_PER_PAGE: number = 20
const props = defineProps<{
  statusFilterOption: StatusFilterOption[]

  fetchResults: (
    values: Partial<FetchValues>,
    signal?: AbortSignal,
  ) => Promise<GetRequestsResponse>
}>()

const formattedQueryParams = computed(() => {
  return mapValues(route.query, (value, key) =>
    MULTISELECT_FILTERS_NAMES.includes(key.toString())
      ? value?.toString().split(',')
      : value,
  )
})

const initialValues = computed(() => {
  return { ...EMPTY_FILTERS_VALUES, ...formattedQueryParams.value }
})

const i18n = useI18n()

const errorTitle = computed(() => {
  if (route.name === ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS)
    return i18n(translations.tasksTechnicalIssueBlockTitle)

  return i18n(translations.requestsTechnicalIssueBlockTitle)
})

const tracking = useTracking()

const currentPage = computed(
  () =>
    parseInt(route.query.page?.toString() || '', 10) || DEFAULT_CURRENT_PAGE,
)

const {
  data: results,
  error,
  status,
  refresh,
} = useAsyncData(`care-data-${String(route.name)}`, callFetchResults, {
  lazy: true,
  immediate: true,
  watch: [() => route.query],
})
const hasData = computed(() => (results.value?.requests || []).length > 0)

const isLoading = computed(
  () => status.value === 'pending' || status.value === 'idle',
)

const pageCount = computed(() =>
  Math.ceil(
    (results?.value?.metadata?.totalCount ?? 0) / REQUEST_COUNT_PER_PAGE,
  ),
)

async function handleApplyFilter(filters: Filter) {
  const nonEmptyQueryParams = ref(useFormattedQueryParams(filters))

  navigateTo({
    name: route.name,
    query: nonEmptyQueryParams.value,
  })
}

function handlePagination({ page }: { page: number }) {
  tracking.trackPaginationClicked({
    name: 'pagination_clicked',
    page_type: route.name as string,
    current_page: currentPage.value,
    target_page: page,
  })

  if (currentPage.value !== page) {
    navigateTo({
      name: route.name,
      query: {
        ...route.query,
        page,
      },
    })
  }
}

async function callFetchResults(): Promise<GetRequestsResponse | undefined> {
  const { page, ...restQuery } = formattedQueryParams.value

  const rawQuery = {
    ...restQuery,
    pageNumber: currentPage.value,
    pageSize: REQUEST_COUNT_PER_PAGE,
  } as Partial<FetchValues>

  const result = await props.fetchResults(rawQuery)

  return result
}
</script>
